/**
 * Application Container App (main.tsx) is used to orchestrate the solutions-dashboard app.
 *
 * This is the first bundle delivered to the browser, intended to be light-weight,
 * with minimal dependencies, so that it can quickly start fetching more expensive requests.
 */

// Polyfill Buffer for Node.js CJS deps (e.g. core-util-is via verror via janus-copilot).
// Must run before any lazy-loaded chunk that transitively requires these packages.
import TagManager from 'react-gtm-module';
import {
  getSession, loadPilot, loadRackerPilot, isRacker, isProd, getEnvironment, isLocal,
} from '@rss-engineering/fabric-ui-session';

/** Keep Session Alive */
import './utils/session-activity-notifier';
import { FEATURE_FLAGS } from './constants/FeatureFlagConstants';

// Set global.isRacker flag like the original app.js did via ApplicationContainer.
// This must happen before any code reads (global as any).isRacker.
(globalThis as any).isRacker = isRacker();

if (typeof (globalThis as any).Buffer === 'undefined') {
  (globalThis as any).Buffer = {
    isBuffer: () => false,
    from: () => ({ toString: () => '' }),
    alloc: () => ({ toString: () => '' }),
  };
}

// Datadog configuration
if (!isLocal()) {
  window.DD_RUM.onReady(() => {
    window.DD_RUM.init({
      applicationId: 'c3fc2db1-6f69-4e59-9355-63c4fb2064a7',
      clientToken: 'pub322419ddb28ef1a974f28b417cbc47f9',
      site: 'datadoghq.com',
      service: 'solutions-dashboard',
      env: getEnvironment().shortName,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      version: __COMMIT_SHA__.slice(0, 7),
      sampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackInteractions: true,
      trackUserInteractions: true,
      trackFrustrations: true,
    });
    window.DD_RUM.startSessionReplayRecording();
  });
}

if (FEATURE_FLAGS.ENABLE_BROWSER_MOCKS) {
  // MSW handlers will be added as mocks are migrated
  import('msw').then(({ setupWorker }) => {
    setupWorker().start();
  });
}

/** Initialize Google Tag Manager */
TagManager.initialize({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  gtmId: isProd() ? __GOOGLE_TAG_MANAGER_KEY_PROD__ : __GOOGLE_TAG_MANAGER_KEY_DEV__,
});

/** Customer Pilot Global Nav / Racker Nav */
const pilotElement = document.querySelector('#nav-pilot');
const appElement = document.querySelector('#app');
const pilotGlobalNav = isRacker()
  ? loadRackerPilot(pilotElement as Element, {
    active: 'racker-dashboard',
    autoCustomerContextResolution: false,
  })
  : loadPilot(pilotElement as Element, {
    'utility-nav': {
      portfolio: {
        section: 'dashboard',
      },
    },
  });

// Fetch app and high priority API calls concurrently.
Promise.all([import('./App'), getSession(), pilotGlobalNav])
  .then(([app, session]) => {
    app.mount(appElement as Element, session);
  });
